import React, { useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { contractApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { signingProjectReturnPath, signingReturnResult } from '../utils/signingReturn';

const MAX_STATUS_ATTEMPTS = 8;
const STATUS_RETRY_DELAY_MS = 1000;

export function SigningReturn() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const contractId = searchParams.get('contractId');

  useEffect(() => {
    if (!projectId || !contractId) return;
    let cancelled = false;
    let retryTimer: number | null = null;

    const finish = (
      result: Parameters<typeof signingProjectReturnPath>[2],
      resolvedProjectId = projectId,
      resolvedContractId = contractId
    ) => {
      if (cancelled) return;
      navigate(
        signingProjectReturnPath(
          resolvedProjectId,
          resolvedContractId,
          result
        ),
        { replace: true }
      );
    };

    const synchronize = async (attempt: number) => {
      try {
        const synced = await contractApi.syncSigning(contractId);
        if (cancelled) return;
        const result = signingReturnResult(synced.contract, user?.personaType);
        const settled = result !== 'pending';
        if (settled || attempt >= MAX_STATUS_ATTEMPTS) {
          finish(result, synced.contract.projectId, synced.contract.id);
          return;
        }
      } catch {
        if (attempt >= MAX_STATUS_ATTEMPTS) {
          finish('error');
          return;
        }
      }

      if (cancelled) return;
      retryTimer = window.setTimeout(() => {
        void synchronize(attempt + 1);
      }, STATUS_RETRY_DELAY_MS);
    };

    void synchronize(1);
    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [contractId, navigate, projectId, user?.personaType]);

  if (!projectId || !contractId) {
    return (
      <main className="min-vh-100 d-flex align-items-center justify-content-center p-3">
        <div className="alert alert-danger mb-0" role="alert">
          <div className="fw-semibold mb-2">{t('contracts.signingReturn.invalid.title')}</div>
          <p>{t('contracts.signingReturn.invalid.description')}</p>
          <Link to="/projects" className="btn btn-outline-danger">
            {t('contracts.signingReturn.invalid.action')}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-vh-100 d-flex align-items-center justify-content-center p-3 text-center" aria-live="polite">
      <div>
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">{t('contracts.signingReturn.loading')}</span>
        </div>
        <h1 className="h4">{t('contracts.signingReturn.title')}</h1>
        <p className="text-muted mb-0">{t('contracts.signingReturn.description')}</p>
      </div>
    </main>
  );
}
